/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// react and redux
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useOutletContext, useParams, useLocation } from 'react-router-dom';
// actions
import { getInstanceCommentsCounts, getInstanceIMICommentsCounts } from '../../../actions';
// store
import { updateNavigationItem } from '../../../store/BASE/navigationSlice';
import { motion } from 'framer-motion';
// BASE components
import BASEPageSimple from '../../../../@BASE/core/BASEPageSimple';
import BASELoading from '../../../../@BASE/core/BASELoading';
import useThemeMediaQuery from '../../../../@BASE/hooks/useThemeMediaQuery';
// material-ui components
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
// custom components
import ChildListingHeader from '../../../shared-components/crud/listing/ChildListingHeader';
import InstanceList from '../../../shared-components/crud/listing/InstanceList';
import CourseStatusSteps from './CourseStatusSteps';
import StatusCard from '../../../shared-components/cards/StatusCard';
import CommentCard from '../../../shared-components/cards/CommentCard';
import InstanceSidebar from '../../../shared-components/crud/instance/InstanceSidebar';

const Root = styled(BASEPageSimple)(({ theme }) => ({
  '& .BASEPageSimple-content': {},
  '& .BASEPageSimple-sidebarHeader': {},
  '& .BASEPageSimple-sidebarContent': {}
}));

const container = {
  show: {
    transition: {
      staggerChildren: 0.04
    }
  }
};
const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const CourseView = () => {
  const { instance, loading } = useOutletContext();
  const dispatch = useDispatch();
  const location = useLocation();
  const routeParams = useParams();
  const pageLayout = useRef(null);
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [commentSummary, setCommentSummary] = useState({
    pending: 0,
    approved: 0,
    disapproved: 0
  });
  const [productionCommentSummary, setProductionCommentSummary] = useState({
    pending: 0,
    approved: 0,
    disapproved: 0
  });
  const getCommentCount = async () => {
    try {
      const countResponse = await dispatch(
        getInstanceCommentsCounts('courses')({
          id: routeParams.id,
          showInternal: 'external'
        })
      ).unwrap();
      setCommentSummary(countResponse);
    } catch (err) {
      console.log(err);
    }
  };
  
  const getProductionCommentCount = async () => {
    try {
      const countResponse = await dispatch(
        getInstanceIMICommentsCounts('courses')({
          id: routeParams.id,
          showInternal: 'external'
        })
      ).unwrap();
      setProductionCommentSummary(countResponse);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (
      location.pathname.includes('/status') ||
      location.pathname.includes('/version')
    ) {
      setRightSidebarOpen(true);
    } else {
      setRightSidebarOpen(false);
    }
  }, [location.pathname]);

  // on mount dispatch getCourse
  useEffect(() => {
    if (instance && !loading) {
      getCommentCount();
      getProductionCommentCount();
    }
    // update navigation
    dispatch(
      updateNavigationItem('course-component', {
        url: `courses/${routeParams.id}`,
        disabled: false
      })
    );
    dispatch(
      updateNavigationItem('lesson-component', {
        url: `lessons`,
        disabled: true
      })
    );
    dispatch(
      updateNavigationItem('section-component', {
        url: `sections`,
        disabled: true
      })
    );
    dispatch(
      updateNavigationItem('page-component', {
        url: `pages`,
        disabled: true
      })
    );
  }, [dispatch, instance]);

  // if course is null return loading
  if (!instance || loading) {
    return <BASELoading />;
  }

  // return course view
  return (
    <Root
      content={
        <div className="w-full my-64 px-24 sm:px-24">
          <motion.div
            className="w-full"
            variants={container}
            initial="hidden"
            animate="show"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-32 w-full">
              <motion.div
                className="col-span-1 lg:col-span-2 flex flex-col flex-auto space-y-16"
                variants={item}
              >
                <ChildListingHeader
                  resource="lessons"
                  subtitle="Modules"
                  parent={instance.id}
                />
                <Divider />
                <InstanceList
                  listing={instance.lessons}
                  resource="lessons"
                />
              </motion.div>
              <motion.div variants={item} className="flex flex-col flex-auto">
                <StatusCard
                  instance={instance}
                  steps={CourseStatusSteps}
                  resource="Course"
                />
                <CommentCard
                  commentSummary={commentSummary}
                  productionCommentSummary={productionCommentSummary}
                  resource="Course"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      }
      ref={pageLayout}
      rightSidebarContent={
        <InstanceSidebar resource="courses" instance={instance} />
      }
      rightSidebarOpen={rightSidebarOpen}
      handleRightSidebarOnClose={() => setRightSidebarOpen(false)}
      rightSidebarWidth={400}
      rightSidebarVariant="temporary"
      scroll={isMobile ? 'normal' : 'normal'}
    />
  );
};

export default CourseView;
